/**
 * Die EINE Aufgaben-Zeile (Aufgaben mit Kontext, Stufe 3): dieselbe Komponente für die
 * Aufgaben-Seite (`pages/tasks.tsx`) und das Dashboard-Widget „Braucht deine Entscheidung“
 * (`pages/dashboard.tsx`) — vorher baute jede Seite ihre eigene Zeile und zeigte dieselbe Aufgabe
 * verschieden an.
 *
 * Jede Zeile beantwortet ohne Klick: welcher Event (Anlass, Datum + Tage bis dahin), für wen
 * (Firma/Kunde, Gäste, Vorgangsstatus), was ist zu tun und warum (Grund-Satz vom Server) — und
 * genau EINE Aktion springt in den richtigen Schritt des Vorgangs. Desktop: Datumsspalte links,
 * rechts die Aktion. Handy: Karte mit dem Kunden als Überschrift, Datum rechts, großer Knopf unten.
 *
 * Wegklicken/Abhaken/Löschen erscheinen nur, wenn der Aufrufer den Handler mitgibt (das Dashboard
 * gibt keinen mit — dort wird nur angesehen und gehandelt, nicht aufgeräumt).
 */
import { gruppeVon, tageText, tageZwischen, type AufgabenZeile } from "../lib/aufgaben-liste";
import { anlassAnzeige, eventStatusMeta } from "./format";
import { Button, Checkbox, Icon, StatusPill } from "./components";

/** „Sa., 7. Nov.“ — Wochentag + Datum des Events, Berliner Kalendertag. */
export function eventTag(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/Berlin" });
}
/** „26.09.“ */
function kurzDatum(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}

/** Fälligkeit als kleiner Hinweis neben der Aktion. */
function FaelligHinweis({ faelligAm, heute }: { faelligAm: string | null; heute: string }) {
  if (!faelligAm) return null;
  const g = gruppeVon(faelligAm, heute);
  const text = g === "ueberfaellig" ? `fällig war ${kurzDatum(faelligAm)}` : g === "heute" ? "fällig heute" : `fällig ${kurzDatum(faelligAm)}`;
  return (
    <span className={`whitespace-nowrap text-[11.5px] font-medium ${g === "ueberfaellig" ? "text-error" : "text-[#9A9080] dark:text-secondary-fixed-dim"}`}>{text}</span>
  );
}

const WEG_KNOPF =
  "shrink-0 self-start !px-1.5 opacity-60 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 md:self-center md:opacity-0";

export function AufgabenZeileAnsicht({
  z,
  heute,
  onAktion,
  onWegklicken,
  onAbhaken,
  onLoeschen,
}: {
  z: AufgabenZeile;
  heute: string;
  onAktion: (ziel: string) => void;
  onWegklicken?: (signature: string) => void;
  onAbhaken?: (id: string) => void;
  onLoeschen?: (id: string) => void;
}) {
  const tage = z.eventDate ? tageZwischen(heute, z.eventDate) : null;
  const kontext = [z.person, z.gaeste != null ? `${z.gaeste} Gäste` : null].filter(Boolean).join(" · ");
  const status = z.eventStatus ? eventStatusMeta(z.eventStatus) : undefined;
  return (
    <li data-testid="aufgaben-zeile" className="group flex gap-3 border-b border-[#F6F0E4] px-4 py-3.5 last:border-b-0 dark:border-white/[0.05] md:items-center md:gap-4 md:px-[18px]">
      {/* Desktop: Datumsspalte */}
      <div className="hidden w-[104px] shrink-0 md:block">
        {z.eventDate ? (
          <>
            <p className="text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{eventTag(z.eventDate)}</p>
            <p className="text-[11.5px] text-[#9A9080] dark:text-secondary-fixed-dim">{tageText(tage!)}</p>
          </>
        ) : (
          <p className="text-[12px] text-[#9A9080] dark:text-secondary-fixed-dim">– ohne Termin</p>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <p className="min-w-0 text-[14px] font-semibold text-on-background dark:text-inverse-on-surface">
            <span className="hidden md:inline">{z.anlass ? `${anlassAnzeige(z.anlass)}${z.kunde ? " · " : ""}` : ""}</span>
            {z.kunde ?? (z.anlass ? <span className="md:hidden">{anlassAnzeige(z.anlass)}</span> : null)}
            {z.nummer && (
              <span data-testid="vorgangsnummer" className="ml-2 whitespace-nowrap text-[11.5px] font-normal text-[#9A9080] dark:text-secondary-fixed-dim">
                {z.nummer}
              </span>
            )}
          </p>
          <span className="shrink-0 whitespace-nowrap text-[12px] text-[#6F675A] dark:text-secondary-fixed-dim md:hidden">
            {z.eventDate ? `${eventTag(z.eventDate)} · ${tageText(tage!)}` : "ohne Termin"}
          </span>
        </div>
        {(kontext || status || z.anlass) && (
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-[#6F675A] dark:text-secondary-fixed-dim">
            {z.anlass && z.kunde && <span className="md:hidden">{anlassAnzeige(z.anlass)}</span>}
            {kontext && <span>{kontext}</span>}
            {status && <StatusPill meta={status} />}
          </div>
        )}
        <p className="mt-1.5 text-[13px] leading-snug text-on-background dark:text-inverse-on-surface">
          <span className="font-semibold">{z.kopf}</span>
          {z.rest && <span className="text-[#6F675A] dark:text-secondary-fixed-dim"> {z.rest}</span>}
        </p>
        {/* Handy: Aktion als großer Knopf unter dem Grund */}
        <div className="mt-2.5 flex items-center gap-2 md:hidden">
          {z.aktion && (
            <Button type="button" variant="secondary" className="flex-1 justify-center" onClick={() => onAktion(z.aktion!.ziel)}>
              {z.aktion.text}
            </Button>
          )}
          <FaelligHinweis faelligAm={z.faelligAm} heute={heute} />
        </div>
      </div>
      {/* Desktop: Fälligkeit + eine Aktion */}
      <div className="hidden shrink-0 items-center gap-3 md:flex">
        <FaelligHinweis faelligAm={z.faelligAm} heute={heute} />
        {z.aktion && (
          <Button type="button" variant="secondary" className="!py-1.5" onClick={() => onAktion(z.aktion!.ziel)}>
            {z.aktion.text}
          </Button>
        )}
      </div>
      {z.auto && onWegklicken && (
        <Button type="button" variant="ghost" className={WEG_KNOPF} title="Erledigt, ausblenden" onClick={() => onWegklicken(z.auto!.signature)}>
          <Icon name="close" className="text-[16px]" />
        </Button>
      )}
      {z.notiz && onAbhaken && (
        <Checkbox
          aria-label={`„${z.notiz.title}“ als erledigt abhaken`}
          title="Erledigt"
          className="mt-1 shrink-0 cursor-pointer self-start md:mt-0 md:self-center"
          onChange={() => onAbhaken(z.notiz!.id)}
        />
      )}
      {z.notiz && onLoeschen && (
        <Button type="button" variant="ghost" className={WEG_KNOPF} title="Löschen" onClick={() => onLoeschen(z.notiz!.id)}>
          <Icon name="close" className="text-[16px]" />
        </Button>
      )}
    </li>
  );
}
